import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Card } from '../../../components/ui/card';
import { Users, MessageSquare, FolderLock, UsersRound, TrendingUp, BarChart3 } from 'lucide-react';

export default function Analytics() {
  const { orgId } = useParams();
  const [metrics, setMetrics] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [orgId]);

  const fetchAnalytics = async () => {
    try {
      const [metricsRes, chartsRes] = await Promise.all([
        apiClient.get(`/analytics/orgs/${orgId}/metrics`),
        apiClient.get(`/analytics/orgs/${orgId}/charts`),
      ]);
      setMetrics(metricsRes.data);
      setCharts(chartsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-600 mt-1">Organization insights and usage metrics</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6 rounded-lg animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-sm text-gray-600">Active Members</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.activeMembers || 0}</p>
            </Card>

            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-enterprise-slate/10 rounded-lg">
                  <UsersRound className="w-5 h-5 text-enterprise-slate" />
                </div>
                <h3 className="text-sm text-gray-600">Teams</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.teamsCount || 0}</p>
            </Card>

            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-success/10 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-success" />
                </div>
                <h3 className="text-sm text-gray-600">AI Messages</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.aiMessagesThisWeek || 0}</p>
              <p className="text-xs text-gray-500 mt-1">This week</p>
            </Card>

            <Card className="p-6 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-personal-warm/10 rounded-lg">
                  <FolderLock className="w-5 h-5 text-personal-warm" />
                </div>
                <h3 className="text-sm text-gray-600">Storage</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics?.storageUsedGB || 0}</p>
              <p className="text-xs text-gray-500 mt-1">GB used</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 rounded-lg">
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                AI Usage Trend
              </h3>
              <div className="space-y-3">
                {charts?.aiUsageWeekly?.map((week, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-16">{week.week}</span>
                    <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden">
                      <div
                        className="h-full bg-primary flex items-center justify-end pr-2"
                        style={{ width: `${(week.messages / 200) * 100}%` }}
                      >
                        <span className="text-xs text-white font-semibold">{week.messages}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 rounded-lg">
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                Team Activity
              </h3>
              <div className="space-y-3">
                {charts?.memberActivity?.map((team, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-900">{team.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-enterprise-slate"
                          style={{ width: `${(team.value / 50) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-8 text-right">
                        {team.value}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
